/**
 * Insets align with `DashboardLayout` (HanoiFloodSidebar left rail + LayerPanel / MapIconLegend right rail).
 */
export const MAP_SIDEBAR_LEFT_PX = 20;
export const MAP_SIDEBAR_WIDTH_PX = 300;
export const MAP_RIGHT_RAIL_OFFSET_PX = 20;
export const MAP_RIGHT_RAIL_WIDTH_PX = 240;
/** Gap between fixed rails and floating map overlays */
export const MAP_OVERLAY_GAP_PX = 12;

/** First pixel inside the map “content” column after the left sidebar */
export const CONTENT_INSET_LEFT_PX =
  MAP_SIDEBAR_LEFT_PX + MAP_SIDEBAR_WIDTH_PX + MAP_OVERLAY_GAP_PX;

/** Clearance from viewport right edge for overlays that must not cover Layer + legend rail */
export const CONTENT_INSET_RIGHT_PX =
  MAP_RIGHT_RAIL_OFFSET_PX + MAP_RIGHT_RAIL_WIDTH_PX + MAP_OVERLAY_GAP_PX;

export const MAP_OVERLAY_Z = {
  tooltip: 40,
  inspector: 50,
  hoverTooltip: 55,
  /** Panel trong cây DOM bản đồ (dưới dải KPI sibling z-95 nếu không portal). */
  detailPanel: 60,
  /**
   * Popup chi tiết ô/lưới ngập — portal `body` + `position: fixed`, trên dải KPI (95) và rail phải (100).
   * Dưới {@link floatingTool} (110) để thanh công cụ vẽ vẫn có thể nổi trên khi cần.
   */
  floodDetailAboveKpi: 105,
  /** Nút mở lại modal phân tích — dưới rail phải */
  spatialToolsPanel: 99,
  modal: 100,
  /** Draw row, forecast toggle — above inspector, below modals */
  floatingTool: 110,
} as const;

/** Thêm lề phải so với CONTENT_INSET_RIGHT để tách khỏi rail lớp / chú giải */
export const SPATIAL_TOOLS_PANEL_EXTRA_RIGHT_GAP_PX = 16;

/** Modal kết quả phân tích — trên app header (1000) */
export const SPATIAL_TOOLS_MODAL_Z_INDEX = 1050;

/** Matches `KPIOverlay` wrapper `top` in `DashboardLayout` */
export const MAP_KPI_OVERLAY_TOP_PX = 20;
/**
 * Left inspector (3D) — below floating KPI strip (~100px) + gap so it does not overlap stat cards.
 */
export const MAP_INSPECTOR_PANEL_TOP_PX = MAP_KPI_OVERLAY_TOP_PX + 120;
/**
 * Popup chi tiết ô/lưới ngập (bên phải) — dưới dải KPI để không đè thẻ phải cùng (vd. Diện tích ngập).
 * Cùng mốc dọc với inspector 3D.
 */
export const MAP_FLOOD_DETAIL_POPUP_TOP_PX = MAP_INSPECTOR_PANEL_TOP_PX;
/**
 * Top of the left sidebar card (`HanoiFloodSidebar`) — leaves a band above for floating search.
 * Matches `DashboardLayout` left rail.
 */
export const MAP_LEFT_RAIL_SIDEBAR_TOP_PX = 76;

/** Viewports at or below this width use mobile map insets (no fixed 332px left rail gap). */
export const MAP_LAYOUT_MOBILE_MAX_PX = 768;

/** Edge padding for floating overlays when rails are hidden (drawers / full-width). */
export const MAP_EDGE_PADDING_PX = 12;

/**
 * Insets for map overlays: desktop aligns with fixed side rails; mobile uses narrow padding only.
 */
export function getMapOverlayInsets(isMobile: boolean): {
  contentInsetLeft: number;
  contentInsetRight: number;
} {
  if (isMobile) {
    return {
      contentInsetLeft: MAP_EDGE_PADDING_PX,
      contentInsetRight: MAP_EDGE_PADDING_PX,
    };
  }
  return {
    contentInsetLeft: CONTENT_INSET_LEFT_PX,
    contentInsetRight: CONTENT_INSET_RIGHT_PX,
  };
}

/**
 * Bottom offsets from viewport for 2D map floating controls (stack upward from bottom edge).
 * Align with TimeSlider area on 3D where applicable.
 */

/**
 * Desktop: single vertical stack from bottom — time slider, flood legend, draw/measure row.
 * Values are conservative so floating cards do not overlap; tune after visual QA.
 */
export const MAP_DOCK_SAFE_BOTTOM_PX = 40;
/** Floating desktop {@link TimeSlider} shell (padding + 2 rows + slider track) */
export const MAP_TIME_SLIDER_DOCK_HEIGHT_PX = 168;
export const MAP_BOTTOM_LEGEND_GAP_PX = 12;
/** 3D bottom flood legend removed — keep 0 so tool row sits just above time slider */
export const MAP_FLOOD_LEGEND_APPROX_HEIGHT_PX = 0;
export const MAP_BOTTOM_TOOLS_GAP_PX = 10;
/** Draw / measure pill row height */
export const MAP_DRAW_MEASURE_ROW_HEIGHT_PX = 48;
/** Gap above draw row for 2D scale line */
export const MAP_STACK_GAP_ABOVE_TOOLS_PX = 8;

/**
 * Kéo hàng công cụ vẽ AOI gần đáy. Khi lớp ngập bật, dùng `getAoiToolbarNudgeDownPx(true)` → 0
 * để hàng công cụ tính theo đúng khoảng dành cho thanh thời gian, không trùng vùng.
 */
export const MAP_AOI_DRAW_TOOLBAR_NUDGE_DOWN_PX = 180;

/**
 * `nudge=0` khi lớp ngập bật: hàng vẽ AOI nằm phía trên khoảng dành cho thanh thời gian đáy (FloodTimeScrubber / TimeSlider).
 */
export function getAoiToolbarNudgeDownPx(floodLayerVisible: boolean): number {
  return floodLayerVisible ? 0 : MAP_AOI_DRAW_TOOLBAR_NUDGE_DOWN_PX;
}

const SAFE_BOTTOM = 'env(safe-area-inset-bottom, 0px)';

export interface MapDesktopBottomStackOptions {
  /** See {@link MAP_AOI_DRAW_TOOLBAR_NUDGE_DOWN_PX} */
  toolsRowNudgeDownPx?: number;
}

/**
 * Desktop-only CSS `bottom` strings for the map dock: centered time slider, left flood legend, tool rows.
 * Order from viewport bottom: time slider → legend → draw/measure → (2D) scale.
 */
export function getMapDesktopBottomStackCss(options?: MapDesktopBottomStackOptions): {
  timeSliderBottom: string;
  legendBottom: string;
  toolsRowBottom: string;
  /** 2D: metric scale line — directly above tool row */
  extrasScaleBottom: string;
} {
  const nudge = Math.max(0, options?.toolsRowNudgeDownPx ?? 0);
  const t = MAP_DOCK_SAFE_BOTTOM_PX;
  const sliderH = MAP_TIME_SLIDER_DOCK_HEIGHT_PX;
  const g1 = MAP_BOTTOM_LEGEND_GAP_PX;
  const legH = MAP_FLOOD_LEGEND_APPROX_HEIGHT_PX;
  const g2 = MAP_BOTTOM_TOOLS_GAP_PX;
  const rowH = MAP_DRAW_MEASURE_ROW_HEIGHT_PX;
  const g3 = MAP_STACK_GAP_ABOVE_TOOLS_PX;
  const legendBottomPx = t + sliderH + g1;
  const toolsRowBottomPx = Math.max(8, legendBottomPx + legH + g2 - nudge);
  const extrasScaleBottomPx = toolsRowBottomPx + rowH + g3;

  return {
    timeSliderBottom: `calc(${t}px + ${SAFE_BOTTOM})`,
    legendBottom: `calc(${legendBottomPx}px + ${SAFE_BOTTOM})`,
    toolsRowBottom: `calc(${toolsRowBottomPx}px + ${SAFE_BOTTOM})`,
    extrasScaleBottom: `calc(${extrasScaleBottomPx}px + ${SAFE_BOTTOM})`,
  };
}

/**
 * @deprecated Use {@link getMapDesktopBottomStackCss}.toolsRowBottom for desktop.
 */
export const MAP_BOTTOM_DRAW_TOOLS_ROW_PX = 100;
/** Spatial analysis card — align with draw row (desktop uses same bottom as tools) */
export const MAP_BOTTOM_SPATIAL_PANEL_PX = 100;

/** Compact map controls — offset from bottom of map container (tight corner) */
export const MAP_NAV_CONTROLS_BOTTOM_PX = 12;
export const MAP_NAV_CONTROLS_GAP_PX = 4;

/**
 * @deprecated Use {@link getMapDesktopBottomStackCss}.extrasScaleBottom.
 */
export const MAP_SCALE_BAR_BOTTOM_PX = 96;

/**
 * Space reserved above {@link MapTimeSliderOverlay} mobile strip (stacked slider height + gap).
 */
export const MAP_MOBILE_TIME_SLIDER_BOTTOM_PX = 12;
export const MAP_TIME_SLIDER_MOBILE_STACK_APPROX_HEIGHT_PX = 228;
export const MAP_TIME_SLIDER_MOBILE_GAP_PX = 12;

/**
 * Bottom edge of 3D mobile legend wrapper: clears stacked time slider
 * ({@link MAP_MOBILE_TIME_SLIDER_BOTTOM_PX} + stack height + gap).
 */
export const MAP_TIME_SLIDER_MOBILE_RESERVED_BOTTOM_PX =
  MAP_MOBILE_TIME_SLIDER_BOTTOM_PX +
  MAP_TIME_SLIDER_MOBILE_STACK_APPROX_HEIGHT_PX +
  MAP_TIME_SLIDER_MOBILE_GAP_PX;

/** @alias {@link MAP_TIME_SLIDER_MOBILE_RESERVED_BOTTOM_PX} */
export const MAP_TIME_SLIDER_RESERVED_BOTTOM_PX = MAP_TIME_SLIDER_MOBILE_RESERVED_BOTTOM_PX;

/** 2D/3D mobile: tool row sits above stacked {@link MapTimeSliderOverlay} */
export function getMapMobileToolsRowBottomCss(options?: MapDesktopBottomStackOptions): string {
  const nudge = Math.max(0, options?.toolsRowNudgeDownPx ?? 0);
  const px = Math.max(
    8,
    MAP_TIME_SLIDER_MOBILE_RESERVED_BOTTOM_PX + MAP_BOTTOM_TOOLS_GAP_PX - nudge,
  );
  return `calc(${px}px + ${SAFE_BOTTOM})`;
}

/** Mobile: metric scale — above draw tools */
export function getMapMobileExtrasScaleBottomCss(options?: MapDesktopBottomStackOptions): string {
  const nudge = Math.max(0, options?.toolsRowNudgeDownPx ?? 0);
  const base = MAP_TIME_SLIDER_MOBILE_RESERVED_BOTTOM_PX + MAP_BOTTOM_TOOLS_GAP_PX - nudge;
  const px = Math.max(8, base) + MAP_DRAW_MEASURE_ROW_HEIGHT_PX + MAP_STACK_GAP_ABOVE_TOOLS_PX;
  return `calc(${px}px + ${SAFE_BOTTOM})`;
}
